import { z } from 'zod';
import { REEL_TYPES, type PublicationKind, type ReelType } from '../kinds.js';
import { limitsFromFormats, passportFormatsSchema, typeFormatsSchema, type TextLimits } from '../schemas/content.js';
import { BODY_BLOCKS, engineStyleSchema, type EngineStyle } from './contract.js';

/*
 * Reading the copy of passport.json (spec 5.2). Engine 0.5.0+ describes its reel
 * types in `types[]`; an older body (the 0.3.0 copy kept until the worker's first
 * sync) has only the top `formats` and reads as one type «Сцены» with every style.
 * Fields of a later engine are skipped, never fatal.
 */

export interface PassportType {
  id: ReelType;
  name: string;
  about: string;
  limits: TextLimits;
  /** Theme ids in the type's order. */
  styles: string[];
}

const typeEntrySchema = z.object({
  id: z.string(),
  name: z.string(),
  about: z.string(),
  formats: typeFormatsSchema,
  styles: z.array(z.string()),
});

const isReelType = (id: string): id is ReelType => (REEL_TYPES as readonly string[]).includes(id);

/**
 * The bot always writes one hook first and one cta last (roles by position), so
 * a body range narrows the type's block range: «Подборка» 4–24 becomes 5–23.
 */
function withRoleCounts(limits: TextLimits, type: ReelType): TextLimits {
  const body = BODY_BLOCKS[type];
  if (!body) return limits;
  return {
    ...limits,
    reel: {
      ...limits.reel,
      minBlocks: Math.max(limits.reel.minBlocks, body[0] + 2),
      maxBlocks: Math.min(limits.reel.maxBlocks, body[1] + 2),
    },
  };
}

/**
 * The types of the passport in its order: only types the bot knows and with
 * valid reel limits. Without usable `types` — one «Сцены» from the top formats.
 * Throws when neither can be read.
 */
export function passportTypes(body: unknown): PassportType[] {
  const b = (body ?? {}) as { types?: unknown; formats?: unknown };
  const out: PassportType[] = [];
  if (Array.isArray(b.types)) {
    for (const raw of b.types) {
      const t = typeEntrySchema.safeParse(raw);
      if (!t.success || !isReelType(t.data.id)) continue;
      out.push({
        id: t.data.id,
        name: t.data.name,
        about: t.data.about,
        limits: withRoleCounts(limitsFromFormats(t.data.formats), t.data.id),
        styles: t.data.styles,
      });
    }
  }
  if (out.length > 0) return out;
  const formats = passportFormatsSchema.safeParse(b.formats);
  if (!formats.success) throw new Error('passport has neither readable types nor formats');
  return [{ id: 'scenes', name: '', about: '', limits: limitsFromFormats(formats.data), styles: styleIds(body) }];
}

function styleIds(body: unknown): string[] {
  const styles = (body as { styles?: unknown } | null)?.styles;
  if (!Array.isArray(styles)) return [];
  return styles.flatMap((s) => (typeof s?.id === 'string' ? [s.id as string] : []));
}

/** null when the passport has no such type; throws on an unreadable body. */
export function passportType(body: unknown, type: ReelType): PassportType | null {
  return passportTypes(body).find((t) => t.id === type) ?? null;
}

/** The text limits of a type; throws when the passport lacks it. */
export function textLimitsFromPassport(body: unknown, type: ReelType): TextLimits {
  const found = passportType(body, type);
  if (!found) throw new Error(`passport has no type ${type}`);
  return found.limits;
}

/** What an idea card may offer, in the order «Рилс · Сцены», «Рилс · Схема», «Рилс · Подборка», «Карусель». */
export function availableKinds(body: unknown): PublicationKind[] {
  let types: PassportType[];
  try {
    types = passportTypes(body);
  } catch {
    return [];
  }
  const scenes = types.find((t) => t.id === 'scenes');
  const kinds: PublicationKind[] = [];
  if (scenes) kinds.push('reel_scenes');
  if (types.some((t) => t.id === 'diagram')) kinds.push('reel_diagram');
  if (types.some((t) => t.id === 'listicle')) kinds.push('reel_listicle');
  if (scenes?.limits.carousel) kinds.push('carousel');
  return kinds;
}

/** The styles of a passport body; none when the body has no valid list. */
export function passportStyles(body: unknown): EngineStyle[] {
  const r = z.object({ styles: z.array(engineStyleSchema) }).safeParse(body);
  return r.success ? r.data.styles : [];
}

/** The themes of a type in the type's order; an id missing from `styles[]` is skipped. */
export function stylesOfType(body: unknown, type: ReelType): EngineStyle[] {
  let found: PassportType | null;
  try {
    found = passportType(body, type);
  } catch {
    return [];
  }
  if (!found) return [];
  const byId = new Map(passportStyles(body).map((s) => [s.id, s]));
  return found.styles.flatMap((id) => {
    const s = byId.get(id);
    return s ? [s] : [];
  });
}

/**
 * The theme a type starts on when nothing else picks one (spec 7.3). Ours, not the
 * engine's first: since 0.10.0 the engine lists the themes of a type alphabetically.
 */
export const DEFAULT_STYLES: Readonly<Record<ReelType, string>> = { scenes: 'night', diagram: 'chalk', listicle: 'glass' };

/** `DEFAULT_STYLES[type]` when the type has it, else its first theme; null without themes. */
export function defaultStyleOfType(styles: ReadonlyArray<{ id: string }>, type: ReelType): string | null {
  const ours = DEFAULT_STYLES[type];
  return styles.some((s) => s.id === ours) ? ours : (styles[0]?.id ?? null);
}
